import { EVENT_NAME as SYNC_STATE_CHANGE_EVENT } from '@adobe-fonts/site/events/SyncStateChangeEvent.js';

import { shouldShowRemoveFamilyConfirmationDialog, showRemoveFamilyConfirmationDialog } from '../../dialogs/RemoveFamilyConfirmationDialog.ts';
import { handleFirstMileFontActivation } from '../../dialogs/FontActivationFirstMileDialog.ts';

import { handleAuthenticationWithPostLoginAction } from '../../../util/authentication_util.js';
import { sendAnalyticsEventWithPage } from '../../../util/new_relic.js';
import { getNotificationService } from '../../../util/notification_service.ts';
import { dispatchRetirementNotificationAlert } from '../../../util/notification_helpers.ts';
import { getData } from '../../../util/script_data.js';
import { syncVariations, unsyncVariations } from '../../../util/Sync.js';

const notificationService = getNotificationService();

export const EVENT_NAME = 'af.variation-card-sync';

export class VariationCardSync {
  constructor(element) {
    this.element = element;
  }

  handleSyncStateChangeEvent = (event) => {
    const button = event.target;
    const card = event.target.closest('af-variation-card, .font-variation-card');
    if (!card) {
      return;
    }

    this.dataAnalytics = {
      fontVariationId: card.dataset.id,
      fontVariationName: card.dataset.variationName,
      familySlug: card.dataset.familySlug
    };

    if (button.synced) {
      this.unsyncVariation(button, card);
    } else {
      sendAnalyticsEventWithPage('sync-font-button.click', this.dataAnalytics);
      this.syncVariation(button, card);
    }
  };

  async syncVariation(button, card) {
    const id = parseInt(card.dataset.id, 10);
    if (!id) {
      return;
    }

    try {
      await this.authenticateSyncVariation(id);
      button.synced = true;
      button.toggleAttribute('synced', true);
      this.element.dispatchEvent(new CustomEvent(EVENT_NAME, { synced: true }));
      this.showSyncSuccessMessage(card);
      dispatchRetirementNotificationAlert('family', card.dataset.familySlug, card.dataset.variationName, true);
    } catch (e) {
      this.showSyncErrorMessage(card);
    }
  }

  async authenticateSyncVariation(id) {
    await handleAuthenticationWithPostLoginAction('activate_font', {
      font_variation_id: id
    });

    await syncVariations([id]);
  }

  showSyncSuccessMessage(card) {
    handleFirstMileFontActivation({
      queryParams: {
        familyName: card.dataset.familyName
      }
    });
  }

  showSyncErrorMessage(card) {
    const i18n = getData('/neue/i18n');
    notificationService.error(
      i18n['neue.browse.family_card']['error_occurred_adding_font'], {
        actionLabel: i18n['neue.browse.family_card']['try_again'],
        callbackAction: () => this.syncVariation(card)
      });
  }

  async unsyncVariation(button, card) {
    const id = parseInt(card.dataset.id, 10);
    if (!id) {
      return;
    }

    await this.confirmVariationUsync();
    try {
      this.unsyncVariationId(id);
      button.synced = false;
      button.toggleAttribute('synced', false);
      this.element.dispatchEvent(new CustomEvent(EVENT_NAME, { synced: false }));
      this.showUnsyncSuccessMessage();
      sendAnalyticsEventWithPage('unsync-font-button.click', this.dataAnalytics);
      dispatchRetirementNotificationAlert('family', card.dataset.familySlug, card.dataset.variationName, false);
    } catch (e) {
      this.showUnsyncErrorMessage();
    }
  }

  async unsyncVariationId(id) {
    await unsyncVariations([id]);
  }

  async confirmVariationUsync() {
    return new Promise(resolve => {
      shouldShowRemoveFamilyConfirmationDialog().then(shouldShow => {
        if (shouldShow) {
          const i18n = getData('/neue/i18n');
          const header = i18n['neue.browse.family_card']['remove_font_confirmation_header'];
          const description = i18n['neue.browse.family_card']['remove_font_confirmation_description'];
          showRemoveFamilyConfirmationDialog(header, description, resolve);
        } else {
          resolve();
        }
      });
    });
  }

  showUnsyncSuccessMessage() {
    const i18n = getData('/neue/i18n');
    notificationService.notice(i18n['neue.browse.family_card']['single_font_removed_success_message']);
  }

  showUnsyncErrorMessage() {
    const i18n = getData('/neue/i18n');

    notificationService.error(
      i18n['neue.browse.family_card']['error_occurred_removing_font']);
  }
}

export function registerEventHandlers(element) {
  const variationSync = new VariationCardSync(element);
  element.addEventListener(
    SYNC_STATE_CHANGE_EVENT, variationSync.handleSyncStateChangeEvent);
}
